/**
 * The object-fit for one moment on screen (FEAT-027 Wave 3).
 *
 * Three surfaces need the same two-stage answer — the sequence viewer, the
 * single moment in a conversation, and a story segment — so the staging lives
 * here once: seed the fit from the `thumbhash` (which encodes the shape before
 * a single byte of media arrives, so the blur placeholder and the media agree),
 * then replace it with the media's real intrinsic size on load.
 *
 * `key` is the identity of what's on screen (a moment or segment id). The
 * measured size is stored WITH that key and ignored the moment the key changes,
 * because a sequence viewer keeps one element across many moments: without
 * this, moment 4's wide shape would still be letterboxing moment 5.
 */
import { useCallback, useRef, useState } from 'react';
import {
  fitForSize,
  fitForThumbhash,
  intrinsicSize,
  thumbhashAspect,
  type MediaFitClass,
} from '../utils/mediaFit';

type Measured = { key: unknown; width: number; height: number };

export interface MediaFitState {
  /** `object-cover` or `object-contain` — drop straight into a className. */
  fitClass: MediaFitClass;
  /**
   * The media's shape, for anything that has to line up WITH the picture
   * rather than with the screen — today that is the overlay layer
   * (FEAT-028 wave 2), which draws the grey bar on the rect the media
   * occupies. Real pixels once the element has decoded; before that, the
   * shape the thumbhash carries, so decoration appears with the blur
   * placeholder instead of popping in a beat later. Null when neither is
   * known yet.
   */
  mediaSize: { width: number; height: number } | null;
  /** Call with the loaded element: `onLoad={(e) => noteMedia(e.currentTarget)}`. */
  noteMedia: (el: HTMLImageElement | HTMLVideoElement | null) => void;
}

export function useMediaFit(
  thumbhash: string | null | undefined,
  key: unknown = null
): MediaFitState {
  const [measured, setMeasured] = useState<Measured | null>(null);

  // Read the current key from a stable callback: MomentVideo hangs a single
  // 'loadedmetadata' listener for the life of the host, so noteMedia must not
  // change identity when the moment does.
  const keyRef = useRef(key);
  keyRef.current = key;

  const noteMedia = useCallback((el: HTMLImageElement | HTMLVideoElement | null) => {
    const size = intrinsicSize(el);
    if (!size) return;
    setMeasured({ key: keyRef.current, ...size });
  }, []);

  const live = measured && measured.key === key ? measured : null;

  const fitClass = live
    ? fitForSize(live.width, live.height)
    : fitForThumbhash(thumbhash);

  // Only the SHAPE matters downstream (an object-fit rect is scale-free), so
  // the pre-decode answer can be the thumbhash's aspect expressed as a size.
  const aspect = live ? null : thumbhashAspect(thumbhash);
  const mediaSize = live
    ? { width: live.width, height: live.height }
    : aspect
      ? { width: aspect * 1000, height: 1000 }
      : null;

  return { fitClass, mediaSize, noteMedia };
}
